import React, { useState, useEffect, useCallback } from 'react';
import { API, useTranslation, useAuth, useSettings } from '../App';
import axios from 'axios';
import { 
  FileText, Clock, CheckCircle2, XCircle, Users, Plus, Trash2, BarChart3,
  PieChart as PieChartIcon, TrendingUp, Table2, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer
} from 'recharts';
import CreateWidgetDialog from '../components/CreateWidgetDialog';

const CHART_COLORS = [
  '#C8102E', '#1F2937', '#6366F1', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
];

const DashboardPage = () => {
  const t = useTranslation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [widgetDataMap, setWidgetDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editWidget, setEditWidget] = useState(null);
  const [deleteWidgetId, setDeleteWidgetId] = useState(null);

  const canCreate = user?.is_superadmin || user?.permissions?.includes?.('dashboard.create');
  const canDelete = user?.is_superadmin || user?.permissions?.includes?.('dashboard.delete');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, widgetsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/widgets`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setWidgets(widgetsRes.data || []);

      // Fetch data for each widget
      const dataMap = {};
      for (const w of (widgetsRes.data || [])) {
        try {
          const res = await axios.get(`${API}/dashboard/widgets/${w.id}/data`);
          dataMap[w.id] = res.data;
        } catch { dataMap[w.id] = null; }
      }
      setWidgetDataMap(dataMap);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteWidget = async () => {
    if (!deleteWidgetId) return;
    try {
      await axios.delete(`${API}/dashboard/widgets/${deleteWidgetId}`);
      toast.success('Widget dihapus');
      setDeleteWidgetId(null);
      fetchData();
    } catch {
      toast.error('Gagal menghapus widget');
    }
  };

  const getChartIcon = (type) => {
    const map = { bar: BarChart3, pie: PieChartIcon, line: TrendingUp, table: Table2 };
    return map[type] || BarChart3;
  };

  const renderWidgetChart = (widget) => {
    const data = widgetDataMap[widget.id];
    if (!data?.chart_data?.length) {
      return <p className="text-sm text-gray-400 text-center py-8">Tidak ada data</p>;
    }

    const { chart_data, value_labels } = data;

    if (widget.chart_type === 'table') {
      return (
        <div className="overflow-auto max-h-64 rounded-md border">
          <table className="w-full text-sm" data-testid={`widget-table-${widget.id}`}>
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">
                  {widget.axis_alias || widget.axis_field}
                </th>
                {value_labels.map((l, i) => (
                  <th key={i} className="px-3 py-2 text-right font-medium text-gray-700">{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart_data.map((row, ri) => (
                <tr key={ri} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{row.category}</td>
                  {value_labels.map((l, i) => (
                    <td key={i} className="px-3 py-2 text-right">{typeof row[l] === 'number' ? row[l].toLocaleString() : row[l] ?? 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (widget.chart_type === 'pie') {
      const dataKey = value_labels[0];
      return (
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={chart_data} dataKey={dataKey} nameKey="category" cx="50%" cy="50%" outerRadius={90} label>
              {chart_data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (widget.chart_type === 'line') {
      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chart_data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {value_labels.map((l, i) => (
              <Line key={i} type="monotone" dataKey={l} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // Default: bar
    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chart_data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="category" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {value_labels.map((l, i) => (
            <Bar key={i} dataKey={l} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="overview-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Dokumen', value: stats?.documents?.total || 0, icon: FileText, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Pending', value: stats?.documents?.pending || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Approved', value: stats?.documents?.approved || 0, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Rejected', value: stats?.documents?.rejected || 0, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Active Users', value: stats?.users?.active || 0, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
  ];

  return (
    <div className="space-y-6" data-testid="overview-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight">Overview</h1>
          <p className="text-gray-500 text-sm mt-0.5">Ringkasan dan statistik dashboard</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="gap-2"
            data-testid="new-widget-btn"
          >
            <Plus size={16} />
            New
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((sc, i) => {
          const Icon = sc.icon;
          return (
            <Card key={i} className="border-gray-200" data-testid={`stat-card-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium">{sc.label}</p>
                    <p className={`text-2xl font-bold mt-1 font-['Outfit'] ${sc.color}`}>{sc.value}</p>
                  </div>
                  <div className={`w-10 h-10 ${sc.bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${sc.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Custom Widget Grid */}
      {widgets.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {widgets.map(widget => {
            const ChartIcon = getChartIcon(widget.chart_type);
            return (
              <Card key={widget.id} className="border-gray-200" data-testid={`widget-card-${widget.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ChartIcon size={16} className="text-gray-400" />
                      <CardTitle className="text-base font-semibold">{widget.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-1">
                      {canCreate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-primary"
                          onClick={() => { setEditWidget(widget); setCreateDialogOpen(true); }}
                          data-testid={`edit-widget-${widget.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                          onClick={() => setDeleteWidgetId(widget.id)}
                          data-testid={`delete-widget-${widget.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {renderWidgetChart(widget)}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State for Widgets */}
      {widgets.length === 0 && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-600">Belum ada custom widget</h3>
            <p className="text-xs text-gray-400 mt-1">Klik tombol "New" untuk menambahkan chart dari data dokumen</p>
          </CardContent>
        </Card>
      )}

      {/* Widget Dialog (Create / Edit) */}
      <CreateWidgetDialog
        open={createDialogOpen}
        onOpenChange={(v) => { setCreateDialogOpen(v); if (!v) setEditWidget(null); }}
        onSaved={fetchData}
        editWidget={editWidget}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteWidgetId} onOpenChange={(open) => !open && setDeleteWidgetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Widget?</AlertDialogTitle>
            <AlertDialogDescription>
              Widget ini akan dihapus dari dashboard. Aksi ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWidget} className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DashboardPage;
